import { memo, useState, useContext } from 'react';
import {
  Card,
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Call,
  LocationOn
} from '@mui/icons-material';
import { AuthContext } from './../../AuthContext/AuthContext';
import GuestLoginModal from '../../GuestLoginModal';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from './../../Api/ApiUrls';
const formatPrice = (price) => {
  // Handle null/undefined price
  if (!price && price !== 0) return "N/A";
  
  const num = Number(price);
  if (isNaN(num)) return "N/A";
  
  if (num >= 10000000) {
    return `${(num / 10000000).toFixed(2)} Cr`;
  } else if (num >= 100000) {
    return `${(num / 100000).toFixed(2)} L`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(2)} K`;
  }
  return num.toLocaleString('en-IN');
};

const formatPropertyId = (id) => {
  return `PR${String(id).padStart(4, '0')}`;
};

const truncateText = (text, maxLength = 20) => {
  if (!text) return "Not specified";
  const clean = text.toString().replace(/\s+/g, " ").trim();
  return clean.length > maxLength ? clean.substring(0, maxLength) + "..." : clean;
};

function PropertyCardImpl({ property, selected, onClick, showAlert }) {
  const { isGuest } = useContext(AuthContext);
    const navigate = useNavigate();
  
  const [locationLoading, setLocationLoading] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);

  // Get property type (handle both camelCase and snake_case)
  const propertyType = property.propertyType || property.property_type || "Land";
  const isLand = propertyType?.toLowerCase().includes("land");
  
  // Get values with proper field mapping
  const minAcres = property.minAcres || property.min_acres || "-";
  const maxAcres = property.maxAcres || property.max_acres || "-";
  
  // Price: For land with budget fields
  let priceValue = property.price;
  if (!priceValue && property.effectivePrice) {
    priceValue = property.effectivePrice;
  } else if (!priceValue && property.minBudget) {
    priceValue = property.minBudget;
  }
  
  const location = property.location || property.city || "Not specified";
  const truncatedCityTown = truncateText(location, 20);
  
  const facing = property.facing || "-";
  const ratio = property.ratio || "-";
  const postedBy = property.postedBy || property.posted_by || "-";
  const roadwidth = property.roadwidth || property.road_width || "-";
  const units = property.units || "-";
  
  // Date handling
  const createdAt = property.createdAt || property.created_at;
  const formattedDate = createdAt ? new Date(createdAt).toLocaleDateString("en-IN") : "-";

  // Check guest and show modal
  const checkGuestAndProceed = (actionCallback) => {
    if (isGuest) {
      setShowGuestModal(true);
      return false;
    }
    actionCallback();
    return true;
  };

  // Handle location click
  const handleLocationClick = (e) => {
    e.stopPropagation();
    
    checkGuestAndProceed(() => {
      setLocationLoading(true);
      
      const lat = property.lat;
      const lng = property.lng;
      const locationText = property.location || property.city;

      const fallbackUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        locationText || `${lat},${lng}`
      )}`;

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      const openInMaps = (url) => {
        setLocationLoading(false);
        window.open(url, "_blank");
      };

      if (lat && lng && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const mapsUrl = isIOS
              ? `http://maps.apple.com/?daddr=${lat},${lng}`
              : `https://www.google.com/maps/dir/?api=1&origin=${pos.coords.latitude},${pos.coords.longitude}&destination=${lat},${lng}&travelmode=driving`;
            openInMaps(mapsUrl);
          },
          (error) => {
            console.error("Error getting user location:", error);
            openInMaps(fallbackUrl);
          },
          { timeout: 10000 }
        );
      } else {
        openInMaps(fallbackUrl);
      }
    });
  };
// Handle call click
const handleCallClick = (e) => {
  e.stopPropagation();
  
  const phoneNumber = property.mobileNo || property.mobile_no;
  
  if (phoneNumber) {
    // Non-guest flow - just copy directly
    const proceedAction = () => {
      navigator.clipboard.writeText(phoneNumber)
        .then(() => {
          if (showAlert) {
            showAlert('success', `Phone number copied: ${phoneNumber}`);
          } else {
            alert(`Phone number copied: ${phoneNumber}`);
          }
        })
        .catch((error) => {
          console.error('Failed to copy:', error);
          if (showAlert) {
            showAlert('failure', 'Failed to copy phone number');
          }
        });
    };
    
    // Check guest status before proceeding
    if (isGuest) {
      setShowGuestModal(true);
    } else {
      proceedAction();
    }
  } else {
    // No phone number in property - try to fetch from API
    const fetchAndCopyNumber = async () => {
      try {
        const userId = property.user_id || property.userId;
        if (!userId) {
          throw new Error('No user ID available');
        }
        
        // Fix: Fetch specific user by ID instead of all users
        const response = await axios.get(`${BASE_URL}/users/${userId}/`);
        const user = response.data;
        
        if (user && user.mobile_no) {
          await navigator.clipboard.writeText(user.mobile_no);
          if (showAlert) {
            showAlert('success', `Phone number copied: ${user.mobile_no}`);
          }
        } else {
          throw new Error('No phone number found for this user');
        }
      } catch (error) {
        console.error('Error fetching phone number:', error);
        if (showAlert) {
          showAlert('failure', 'No contact information available');
        } else {
          alert('No contact information available');
        }
      }
    };
    
    if (isGuest) {
      setShowGuestModal(true);
    } else {
      fetchAndCopyNumber();
    }
  }
};
  // Handle card click
  const handleCardClick = (e) => {
    const isButtonClick = e.target.closest('button') || e.target.closest('svg');
    if (!isButtonClick && onClick) {
      if (isGuest) {
        setShowGuestModal(true);
        return;
      }
      localStorage.setItem('lastViewedPropertyId', property.id);

        navigate(`/jv-jv-property-detail/${property.id}`);
    }
  };

  return (
    <>
      <Card
        onClick={handleCardClick}
        sx={{
          height: 135,
          width: 275,
          borderRadius: "14px",
          border: selected ? '2px solid #FFD600' : '2px solid #000',
          display: "flex",
          alignItems: "center",
          bgcolor: "#fff",
          cursor: "pointer",
          transform: selected ? 'translateY(-2px)' : 'none',
          transition: 'transform 200ms, border-color 200ms',
          boxShadow: selected ? '0 4px 12px rgba(0,0,0,0.5)' : 'none',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            px: 0.8,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          {/* Top Row */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.8 }}>
            <Typography fontSize="10px" noWrap sx={{ lineHeight: 1 }}>
              <b>Property ID:</b> {formatPropertyId(property?.id || property?.req_id)}
            </Typography>

            <Typography
              fontSize="10px"
              sx={{
                backgroundColor: "#000000cb",
                color: "#fff",
                borderRadius: "50px",
                px: 0.8,
                py: "2px",
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              ₹ {formatPrice(priceValue)}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 0.3,
            }}
          >
            {isLand ? (
              <>
                <Box sx={{ display: "flex" }}>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Type:</b> {propertyType}
                  </Typography>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Location:</b> {truncatedCityTown}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Min/Max Acres:</b> {minAcres}/{maxAcres}
                  </Typography>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Facing:</b> {facing}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Ratio:</b> {ratio}
                  </Typography>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Posted By:</b> {postedBy}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Road Width:</b> {roadwidth} {roadwidth !== "-" ? "ft" : ""}
                  </Typography>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Date:</b> {formattedDate}
                  </Typography>
                </Box>
              </>
            ) : (
              /* PLOT LAYOUT */
              <>
                <Box sx={{ display: "flex" }}>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Type:</b> {propertyType}
                  </Typography>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Location:</b> {truncatedCityTown}
                  </Typography>
                </Box>

               <Box sx={{ display: "flex" }}>
  <Typography
    fontSize="9px"
    noWrap
    sx={{ width: "50%", lineHeight: 1.2 }}
  >
    <b>Site Area:</b>{" "}
    {property.site_area
      ? property.site_area
      : property.length && property.width
      ? property.length * property.width
      : "-"}
  </Typography>

  <Typography
    fontSize="9px"
    noWrap
    sx={{ width: "50%", lineHeight: 1.2 }}
  >
    <b>Units:</b>{" "}
    {property.units
      ? ["ft", "m"].includes(property.units.toLowerCase())
        ? `sq.${property.units}`
        : property.units
      : "-"}
  </Typography>
</Box>

                <Box sx={{ display: "flex" }}>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Ratio:</b> {ratio}
                  </Typography>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Posted By:</b> {postedBy}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Road Width:</b> {roadwidth} {roadwidth !== "-" ? "ft" : ""}
                  </Typography>
                  <Typography fontSize="9px" noWrap sx={{ width: "50%", lineHeight: 1.2 }}>
                    <b>Facing:</b> {facing}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Bottom Buttons */}
          <Box sx={{ display: "flex", gap: 0.5, mb: "6px" }}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={<Call />}
              onClick={handleCallClick}
              sx={{ fontSize: "9px", py: 0.3, textTransform: 'none' }}
            >
              Call
            </Button>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              startIcon={locationLoading ? null : <LocationOn />}
              onClick={handleLocationClick}
              disabled={locationLoading}
              sx={{ fontSize: "9px", py: 0.3, textTransform: 'none' }}
            >
              {locationLoading ? <CircularProgress size={14} /> : "Map"}
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Guest Login Modal */}
      <GuestLoginModal
        open={showGuestModal}
        onClose={() => {
          setShowGuestModal(false);
        }}
      />
    </>
  );
}

export default memo(PropertyCardImpl, (prevProps, nextProps) => {
  return prevProps.property?.id === nextProps.property?.id && 
         prevProps.selected === nextProps.selected;
});